import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, Award, Clock, FileCheck, Handshake, 
  ArrowRight, CheckCircle2, Building, Landmark 
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const WhyUs = () => {
  const differentiators = [
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: 'D2D Model',
      description: 'Our Documentation to Disbursement approach ensures complete end-to-end support, eliminating gaps and ensuring seamless execution.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Expert Team',
      description: 'Led by qualified Chartered Accountants with combined experience of over 30 years in finance, banking, and government schemes.'
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: 'Strong Relationships',
      description: 'Established partnerships with major banks, NBFCs, and government bodies ensure faster processing and better terms.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Transparent Approach',
      description: 'Clear fee structures, honest assessments, and regular updates throughout the process. No hidden charges, no surprises.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: '98% Success Rate',
      description: 'Our meticulous approach and deep expertise result in exceptionally high approval rates for subsidies and loans.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Timely Delivery',
      description: 'Efficient processes and proactive follow-ups ensure your applications are processed within optimal timelines.'
    }
  ];

  const bankPartners = [
    'ICICI Bank', 'HDFC Bank', 'Axis Bank', 'State Bank of India', 
    'Kotak Mahindra', 'Yes Bank', 'IndusInd Bank', 'Punjab National Bank'
  ];

  const clientTypes = [
    {
      icon: <Building className="w-10 h-10" />,
      title: 'MSMEs',
      items: [
        'Manufacturing units',
        'Service enterprises',
        'Trading businesses',
        'Startups'
      ]
    },
    {
      icon: <Landmark className="w-10 h-10" />,
      title: 'Corporates',
      items: [
        'Large enterprises',
        'Group companies',
        'Industrial units',
        'Export houses'
      ]
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Individuals',
      items: [
        'First-time entrepreneurs',
        'Home buyers',
        'Business professionals',
        'Salaried individuals'
      ]
    }
  ];

  return (
    <div data-testid="why-us-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="why-us-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-6 shadow-sm">
                Why Choose Us
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#0f172a] leading-tight mb-6">
                A Decade of
                <span className="gradient-text block">Trust & Excellence</span>
              </h1>
              <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                Since 2014, Thesaurus of Finance has been a trusted partner for businesses 
                and individuals seeking expert guidance in government subsidies and financial solutions. 
                Our commitment to transparency, expertise, and results sets us apart.
              </p>
              <div className="flex flex-wrap gap-8 mb-8">
                <div>
                  <p className="stat-number text-4xl font-semibold">500+</p>
                  <p className="text-[#64748b] text-sm">Clients Served</p>
                </div>
                <div>
                  <p className="stat-number text-4xl font-semibold">₹100Cr+</p>
                  <p className="text-[#64748b] text-sm">Funds Facilitated</p>
                </div>
                <div>
                  <p className="stat-number text-4xl font-semibold">98%</p>
                  <p className="text-[#64748b] text-sm">Success Rate</p>
                </div>
              </div>
              <Link
                to="/contact"
                data-testid="why-us-cta"
                className="inline-flex items-center gap-2 gradient-btn px-8 py-4 rounded-full font-medium text-white"
              >
                Start Your Journey
                <ArrowRight size={18} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.pexels.com/photos/955394/pexels-photo-955394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional consultation"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-24" data-testid="differentiators-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Our unique approach and commitment to excellence make us the preferred choice for financial consultancy.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full service-card border border-slate-100 hover:border-[#00bcd4]/30 bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mb-6">
                      {item.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#64748b] text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* D2D Process Detailed */}
      <section className="py-24 bg-[#0f172a]" data-testid="d2d-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium text-[#00bcd4] mb-6">
                Our Approach
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
                D2D Model — Your End-to-End Partner
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Our Documentation to Disbursement model ensures you have expert support 
                at every step of your financial journey. We don't just file paperwork — 
                we actively manage and follow up until successful completion.
              </p>
              <div className="space-y-4">
                {[
                  'Initial assessment and eligibility evaluation',
                  'Complete documentation preparation and verification',
                  'Application submission to relevant authorities',
                  'Active follow-up and status tracking',
                  'Liaison with banks and government departments',
                  'Support until final disbursement'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00bcd4] flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { number: '01', label: 'Documentation' },
                { number: '02', label: 'Verification' },
                { number: '03', label: 'Processing' },
                { number: '04', label: 'Disbursement' }
              ].map((step, index) => (
                <div
                  key={step.number}
                  className={`p-8 rounded-2xl ${
                    index % 2 === 0 ? 'bg-white/5' : 'bg-gradient-to-br from-[#3b5998]/20 to-[#00bcd4]/20'
                  }`}
                >
                  <span className="text-5xl font-bold text-white/20 font-mono block mb-2">
                    {step.number}
                  </span>
                  <span className="text-white font-medium">{step.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-24" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Who We Serve
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Our services cater to diverse client segments with tailored solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {clientTypes.map((client, index) => (
              <motion.div
                key={client.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="h-full border border-slate-100 hover:border-[#00bcd4]/30 transition-colors bg-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mx-auto mb-6">
                      {client.icon}
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-6">
                      {client.title}
                    </h3>
                    <ul className="space-y-3">
                      {client.items.map((item, i) => (
                        <li key={i} className="text-[#64748b] text-sm">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Banking Partners */}
      <section className="py-24 bg-[#f8fafc]" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Our Banking Partners
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Strong relationships with leading financial institutions for better service.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {bankPartners.map((bank, index) => (
              <motion.div
                key={bank}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="px-6 py-3 bg-white rounded-full border border-slate-200 text-[#0f172a] text-sm font-medium shadow-sm"
              >
                {bank}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="why-us-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl bg-gradient-to-r from-[#3b5998] to-[#00bcd4] p-12 lg:p-16 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            </div>
            <div className="relative text-center">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
                Experience the Difference
              </h2>
              <p className="text-white/80 max-w-2xl mx-auto mb-8">
                Join 500+ satisfied clients who have achieved their financial goals with our expert guidance.
              </p>
              <Link
                to="/contact"
                data-testid="why-us-contact-cta"
                className="px-10 py-4 bg-white text-[#3b5998] rounded-full font-medium hover:bg-white/90 transition-colors inline-flex items-center gap-2"
              >
                Schedule Consultation
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
